/**
 * PWA install support. Chrome fires `beforeinstallprompt` once, early, often
 * before the Alerts screen mounts, so it is captured here at app start
 * (imported from main.tsx) and kept in a module singleton for the Install card.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let captured = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function initPwaInstallCapture(win: Window = window): void {
  if (captured) return;
  captured = true;
  win.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  win.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export function getInstallPrompt(): BeforeInstallPromptEvent | null { return deferred; }
export function wasInstalled(): boolean { return installed; }
export function subscribeInstall(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Shows the native prompt; 'unavailable' when the browser gave us no event. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = deferred;
  if (!ev) return 'unavailable';
  deferred = null; // a prompt event can only be used once
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  if (outcome === 'accepted') installed = true;
  notify();
  return outcome;
}

export function isStandaloneDisplay(win: Window = window): boolean {
  return !!win.matchMedia?.('(display-mode: standalone)').matches
    || (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type InstallPlatform = 'ios' | 'android' | 'other';
export function installPlatform(userAgent: string, platform = '', maxTouchPoints = 0): InstallPlatform {
  if (/iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  return 'other';
}

/** Manual steps shown when there is no native prompt to call. */
export function manualInstallSteps(p: InstallPlatform): string[] {
  if (p === 'ios') return ['Tap the Share button in Safari.', 'Choose "Add to Home Screen".', 'Tap Add.'];
  if (p === 'android') return ['Tap the menu ⋮ in Chrome.', 'Choose "Install app" or "Add to Home screen".', 'Confirm Install.'];
  return ['Open your browser menu.', 'Choose "Install app" or "Add to Home screen".'];
}

/** Test hook. */
export function __resetPwaInstall(): void { deferred = null; installed = false; captured = false; listeners.clear(); }
